import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Outlet } from 'react-router-dom';
import { isPointInPolygon } from './services/utils/geoUtils';
// 🔥 修正：使用正確的函數名稱
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import { addDays, startOfDay } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { supabase } from './services/supabase';

// 🔥 定義餐廳時區
const RESTAURANT_TIMEZONE = 'America/Vancouver';

// --- 📍 地址轉座標輔助函式 (使用 /api/geocode proxy) ---
const getCoordinatesFromAddress = async (fullAddress) => {
  try {
    const res = await fetch(`/api/geocode?text=${encodeURIComponent(fullAddress)}`);
    if (!res.ok) return null;
    const data = await res.json();
    const best = data?.results?.[0];
    if (best && Number.isFinite(best.lat) && Number.isFinite(best.lon)) {
      return { lat: best.lat, lng: best.lon };
    }
  } catch (error) {
    // silently ignore
  }
  return null;
};

// --- 樣式定義 ---
const RestaurantBookingStyles = () => (
  <style>{`
    .booking-container {
      max-width: 1400px;
      margin: 0 auto;
      padding: clamp(15px, 3vw, 40px) clamp(15px, 3vw, 20px);
      box-sizing: border-box;
      font-family: 'Plus Jakarta Sans', 'Noto Sans TC', sans-serif;
    }
    .loading-container {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      height: 50vh;
      font-size: 1.2rem;
      color: #666;
    }
    .error-message {
      color: #e53e3e;
      margin-top: 10px;
      text-align: center;
    }
    .env-warning {
      background-color: #fff3cd;
      color: #856404;
      padding: 10px;
      margin-bottom: 20px;
      border-radius: 4px;
      text-align: center;
    }
    @media (max-width: 768px) {
      .booking-container { padding: 15px 10px 30px 10px; }
    }
  `}</style>
);

const RestaurantBooking = ({ isLoggedIn, requestLogin, user }) => {
  const navigate = useNavigate();
  const { bookingMode } = useParams();
  const { t, i18n } = useTranslation('booking');
  const isEn = i18n.language === 'en';

  // --- 1. 資料庫菜單與載入狀態 ---
  const [dbMenus, setDbMenus] = useState([]);
  const [categoriesData, setCategoriesData] = useState([]);
  const [modifierGroupsData, setModifierGroupsData] = useState([]);

  // --- 2. 配送區域相關 State ---
  const [allZones, setAllZones] = useState([]);
  const [matchedZone, setMatchedZone] = useState(null);

  // --- 全單折扣 ---
  const [globalDiscount, setGlobalDiscount] = useState(null); // { enabled, rate, label }
  const [packagingFee, setPackagingFee] = useState(0);
  const [needCutlery, setNeedCutlery] = useState(true);

  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [envError, setEnvError] = useState(false);

  // --- 🔥 修改：日期生成邏輯（使用溫哥華時區）---
  const [next7Days, setNext7Days] = useState([]);

  useEffect(() => {
    const days = [];
    const weekdays = t('weekdays', { returnObjects: true });
    const monthsShort = t('months_short', { returnObjects: true });

    // 🔥 修正：使用 toZonedTime
    const nowInVancouver = toZonedTime(new Date(), RESTAURANT_TIMEZONE);
    const todayInVancouver = startOfDay(nowInVancouver);

    const startOffset = bookingMode === 'single' ? 0 : 1;
    for (let i = startOffset; i < startOffset + 7; i++) {
      // 在溫哥華時區計算未來 7 天
      const futureDate = addDays(todayInVancouver, i);

      // 提取年月日（溫哥華時區）
      const year = futureDate.getFullYear();
      const month = futureDate.getMonth() + 1;
      const day = futureDate.getDate();

      days.push({
        id: i,
        date: futureDate, // 保留 Date 物件供顯示用
        dateString: `${month}/${day}`,
        weekday: weekdays[futureDate.getDay()],
        fullDate: t('full_date', { month: monthsShort[month - 1], day }),

        // 🔥 關鍵：明確標記這是溫哥華時區的日期（YYYY-MM-DD格式）
        isoDateStr: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T00:00:00.000Z`
      });
    }

    setNext7Days(days);
  }, [t, bookingMode]);

  // --- 4. 抓取資料 (菜單 + 配送區域) ---
  useEffect(() => {
    const fetchData = async () => {
      if (!process.env.REACT_APP_SUPABASE_URL || !process.env.REACT_APP_SUPABASE_ANON_KEY) {
        setEnvError(true);
        setIsLoading(false);
        setDbMenus([{ id: 1, name: "預覽餐點", price: 10, categories: [1], modifier_groups: [] }]);
        setCategoriesData([{ id: 1, name: "主餐" }]);
        setModifierGroupsData([]);
        return;
      }

      try {
        setIsLoading(true);

        // --- A. 抓取分類 ---
        const { data: catsData, error: catsError } = await supabase
          .from('categories')
          .select('*')
          .eq('is_hidden', false)
          .order('order', { ascending: true });
        if (catsError) throw catsError;
        // DB: alternate_name=中文, name=英文
        const cats = Array.isArray(catsData) ? catsData : [];
        setCategoriesData(cats.map(cat => ({
          ...cat,
          name: isEn ? (cat.name || '') : (cat.alternate_name || cat.name),
          alternate_name: isEn ? '' : cat.name,
        })));

        // --- B. 抓取菜單 ---
        const { data: itemsData, error: itemsError } = await supabase
          .from('menu_items')
          .select('*')
          .eq('is_active', true)
          .eq('is_hidden', false)
          .order('fee_in_cents', { ascending: true });
        if (itemsError) throw itemsError;

        const items = Array.isArray(itemsData) ? itemsData : [];
        const formattedMenus = items.map(item => ({
          ...item,
          // DB: alternate_name=中文, name=英文; description=中文, description_en=英文
          name_en: item.name || '',
          name_zh: item.alternate_name || item.name,
          name: isEn ? (item.name || '') : (item.alternate_name || item.name),
          alternate_name: isEn ? '' : item.name,
          description: isEn ? (item.description_en || '') : item.description,
          price: typeof item.fee_in_cents === 'number' ? item.fee_in_cents / 100 : 0,
          categories: Array.isArray(item.categories) ? item.categories : [],
          modifier_groups: Array.isArray(item.modifier_groups) ? item.modifier_groups : []
        }));
        setDbMenus(formattedMenus);

        // --- C. 抓取配料 ---
        const { data: groupsData, error: groupsError } = await supabase
          .from('modifier_groups')
          .select('*')
          .order('order', { ascending: true, nullsFirst: false });
        if (groupsError) throw groupsError;

        const { data: modsData, error: modsError } = await supabase
          .from('modifiers')
          .select('*')
          .eq('is_available', true)
          .order('order', { ascending: true, nullsFirst: false });
        if (modsError) throw modsError;

        const groups = Array.isArray(groupsData) ? groupsData : [];
        const modifiers = Array.isArray(modsData) ? modsData : [];

        const assembledGroups = groups.map(group => ({
          ...group,
          // DB: alternate_name=中文, name=英文
          name: isEn ? (group.name || '') : (group.alternate_name || group.name),
          alternate_name: isEn ? '' : group.name,
          modifiers: modifiers
            .filter(mod => mod.modifier_group_id === group.id)
            .map(mod => ({
              ...mod,
              name_en: mod.name || '',
              name_zh: mod.alternate_name || mod.name,
              name: isEn ? (mod.name || '') : (mod.alternate_name || mod.name),
              alternate_name: isEn ? '' : mod.name,
              price: typeof mod.fee_in_cents === 'number' ? mod.fee_in_cents / 100 : 0
            }))
            .sort((a, b) => (a.order || 0) - (b.order || 0))
        }));
        setModifierGroupsData(assembledGroups);

        // --- D. 抓取配送區域（依 bookingMode 篩選）---
        const zoneType = bookingMode === 'single' ? 'single' : 'weekly';
        const { data: zonesData, error: zonesError } = await supabase
          .from('delivery_zones')
          .select('*')
          .eq('zone_type', zoneType);

        if (zonesError) {
            setAllZones([]);
        } else {
            setAllZones(zonesData || []);
        }

        // --- E. 抓取全單折扣設定 ---
        if (zoneType === 'single') {
          const { data: discountData } = await supabase
            .from('restaurant_settings')
            .select('value')
            .eq('key', 'global_discount')
            .single();
          if (discountData?.value) {
            setGlobalDiscount(discountData.value);
          }
        }

        // --- F. 抓取外賣包裝費 ---
        const { data: pkgData } = await supabase
          .from('restaurant_settings')
          .select('value')
          .eq('key', 'packaging_fee')
          .single();
        if (pkgData?.value !== undefined && pkgData?.value !== null) {
          setPackagingFee(Number(pkgData.value));
        }

      } catch (error) {
        let errorMessage = t('errors.load_failed');
        if (error.message?.includes('network')) {
          errorMessage += t('errors.network');
        } else {
          errorMessage += `：${error.message || '未知錯誤'}`;
        }
        setFetchError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [isEn, bookingMode]);

  // --- 4.1 Realtime 訂閱菜單售罄狀態 ---
  useEffect(() => {
    if (!process.env.REACT_APP_SUPABASE_URL || !process.env.REACT_APP_SUPABASE_ANON_KEY) {
      return;
    }

    const channel = supabase
      .channel('menu-availability')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'menu_items',
        },
        (payload) => {
          const updatedItem = payload.new;

          setDbMenus(prev => prev.map(item => {
            if (item.id === updatedItem.id) {
              return {
                ...item,
                is_available: updatedItem.is_available,
              };
            }
            return item;
          }));
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // --- 5. 核心邏輯：判斷地址在哪個 Zone ---
  const detectZone = (lat, lng) => {
    if (!lat || !lng || allZones.length === 0) {
        setMatchedZone(null);
        return null;
    }

    const found = allZones.find(zone => isPointInPolygon([lat, lng], zone.boundaries));

    if (found) {
        setMatchedZone(found);
    } else {
        setMatchedZone(null);
    }
    return found;
  };

  // --- 6. 訂單狀態管理 ---
  const [selectedDays, setSelectedDays] = useState(() => {
    return bookingMode === 'single' ? [0] : [];
  });

  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [unitNumber, setUnitNumber] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [province, setProvince] = useState('BC');

  const [mealSelections, setMealSelections] = useState(() => {
    return bookingMode === 'single' ? { 0: { lunch: null, dinner: null } } : {};
  });

  const [selectedTime, setSelectedTime] = useState({});
  const [pendingAction, setPendingAction] = useState(null);

  // --- Single mode 新增 state ---
  const [orderType, setOrderType] = useState('pickup'); // 'pickup' | 'delivery'
  const [cartItems, setCartItems] = useState([]);
  const [fulfillmentMode, setFulfillmentMode] = useState('instant'); // 'instant' | 'scheduled'
  const [scheduledTime, setScheduledTime] = useState(null);
  const [scheduledDate, setScheduledDate] = useState(null); // 'YYYY-MM-DD'
  const [deliveryNotes, setDeliveryNotes] = useState('');

  const MAX_DAYS = 7;
  const MIN_DAYS = bookingMode === 'single' ? 1 : 2;
  const menuOptions = dbMenus || [];

  const toggleDay = (dayId) => {
    if (bookingMode === 'single') {
      setSelectedDays([dayId]);
      return;
    }
    if (selectedDays.includes(dayId)) {
      setSelectedDays(selectedDays.filter(id => id !== dayId));
      const newMeals = { ...mealSelections };
      delete newMeals[dayId];
      setMealSelections(newMeals);
      const newTimes = { ...selectedTime };
      delete newTimes[`${dayId}_lunch`];
      delete newTimes[`${dayId}_dinner`];
      setSelectedTime(newTimes);
    } else {
      if (selectedDays.length >= MAX_DAYS) {
        alert(t('date_selection.alert_max_days', { max: MAX_DAYS }));
        return;
      }
      setSelectedDays([...selectedDays, dayId].sort((a, b) => a - b));
    }
  };

  const removeDay = (dayId) => toggleDay(dayId);

  const selectMealForDay = (dayId, selectionData, type = 'lunch') => {
    setMealSelections(prev => ({
      ...prev,
      [dayId]: {
        ...prev[dayId],
        [type]: selectionData
      }
    }));
  };

  const calculateTotal = () => {
    let total = 0;

    Object.values(mealSelections).forEach(daySelection => {
      if (!daySelection) return;

      ['lunch', 'dinner'].forEach(type => {
        const selection = daySelection[type];
        if (!selection || !selection.id) return;

        const meal = menuOptions.find(m => m.id === selection.id);
        if (!meal) return;

        if (typeof meal.price === 'number') {
          total += meal.price;
        }
        if (Array.isArray(selection.modifiers)) {
          selection.modifiers.forEach(mod => {
            if (mod && typeof mod.price === 'number') {
              total += mod.price;
            }
          });
        }
      });
    });

    return isNaN(total) ? '0.00' : total.toFixed(2);
  };

  // --- 購物車函式 (Single mode) ---
  const addToCart = (dish, modifiers = [], quantity = 1, note = '') => {
    const cartId = `cart_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    setCartItems(prev => [...prev, {
      cartId,
      menuItemId: dish.id,
      name: dish.name,
      name_en: dish.name_en || dish.name,
      name_zh: dish.name_zh || dish.name,
      price: dish.price,
      emoji: dish.emoji || '',
      image_url: dish.image_url || '',
      tax_gst: dish.tax_gst || false,
      tax_pst: dish.tax_pst || false,
      modifiers: modifiers.map(mod => ({
        id: mod.id,
        name: mod.name,
        name_en: mod.name_en || mod.name,
        name_zh: mod.name_zh || mod.name,
        alternate_name: mod.alternate_name || mod.name,
        price: mod.price || 0,
      })),
      quantity,
      ...(note ? { note } : {}),
    }]);
  };

  const updateCartItemQuantity = (cartId, newQty) => {
    if (newQty <= 0) {
      setCartItems(prev => prev.filter(item => item.cartId !== cartId));
    } else {
      setCartItems(prev => prev.map(item =>
        item.cartId === cartId ? { ...item, quantity: newQty } : item
      ));
    }
  };

  const removeCartItem = (cartId) => {
    setCartItems(prev => prev.filter(item => item.cartId !== cartId));
  };

  const calculateCartTotal = () => {
    return cartItems.reduce((sum, item) => {
      const modTotal = item.modifiers.reduce((ms, mod) => ms + (mod.price || 0), 0);
      return sum + (item.price + modTotal) * item.quantity;
    }, 0);
  };

  const handleMealSelectionNext = () => {
    // Single mode: check cart instead
    if (bookingMode === 'single') {
      if (cartItems.length === 0) {
        alert(t('single_meal.alert_empty_cart'));
        return;
      }
      navigate('confirm');
      return;
    }
    const hasAnyMeal = selectedDays.every(dayId => {
      const sel = mealSelections[dayId];
      return sel && (sel.lunch || sel.dinner);
    });
    if (!hasAnyMeal) {
      alert(t('meal_selection.alert_select_meals'));
      return;
    }
    navigate('time');
  };

  const handleSubmitTime = () => {
    if (!isLoggedIn) {
      setPendingAction(bookingMode === 'single' ? 'goToConfirm' : 'goToPayment');
      requestLogin();
    } else {
      navigate(bookingMode === 'single' ? 'confirm' : 'payment', { replace: true });
    }
  };

  // --- 7. 生成隨機訂單編號 ---
  const generateOrderNumber = () => {
    const chars = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return `ORD-${result}`;
  };

  // --- 🔥 修改：處理付款與訂單儲存（使用溫哥華時區的日期）---
  const handlePayment = async (paymentFormData, orderBreakdown, stripePaymentIntentId) => {
    // 輔助：確保金額是兩位小數的浮點數
    const safeFix = (num) => parseFloat(Number(num || 0).toFixed(2));

    // --- Single mode cart branch ---
    if (bookingMode === 'single' && cartItems.length > 0) {
      // Validate delivery address before proceeding
      if (orderType === 'delivery') {
        if (!deliveryAddress || !city) {
          alert(t('errors.missing_address', '請填寫完整的配送地址'));
          throw new Error('Missing delivery address');
        }
      }

      const cartItemsData = cartItems.map(item => ({
        id: item.menuItemId,
        name: item.name,
        name_en: item.name_en || item.name,
        name_zh: item.name_zh || item.name,
        price: item.price,
        emoji: item.emoji,
        image_url: item.image_url,
        modifiers: item.modifiers.map(mod => ({
          ...mod,
          name_en: mod.name_en || mod.name,
          name_zh: mod.name_zh || mod.name,
        })),
        quantity: item.quantity,
        ...(item.note ? { note: item.note } : {}),
      }));

      let coords = null;
      if (orderType === 'delivery' && deliveryAddress && city) {
        const fullAddressStr = `${deliveryAddress}, ${city}, ${province}`;
        coords = await getCoordinatesFromAddress(fullAddressStr);
      }

      const newOrderNumber = generateOrderNumber();

      const deliveryInfo = {
        order_type: orderType,
        fulfillment_mode: fulfillmentMode,
        scheduled_time: scheduledDate && scheduledTime
          ? `${scheduledDate} ${scheduledTime}`
          : scheduledTime,
        delivery_notes: deliveryNotes || paymentFormData.deliveryNotes || '',
        need_cutlery: needCutlery,
      };

      if (orderType === 'delivery') {
        deliveryInfo.address = deliveryAddress;
        deliveryInfo.unit = unitNumber;
        deliveryInfo.city = city;
        deliveryInfo.postal_code = postalCode;
        deliveryInfo.province = province;
        deliveryInfo.zone_id = matchedZone?.id || null;
        deliveryInfo.zone_name = matchedZone?.name || null;
        deliveryInfo.lat = coords ? coords.lat : null;
        deliveryInfo.lng = coords ? coords.lng : null;
      }

      const orderPayload = {
        user_id: user?.id || null,
        order_number: newOrderNumber,
        booking_mode: bookingMode,
        contact_info: {
          name: paymentFormData.contactName,
          email: paymentFormData.email || user?.email || '',
          phone: paymentFormData.contactPhone,
          payment_method: 'credit_card',
          card_holder: paymentFormData.cardHolder,
          stripe_payment_intent_id: stripePaymentIntentId || null,
        },
        delivery_info: deliveryInfo,
        delivery_tracking: {},
        items_data: { cart_items: cartItemsData },
        delivery_schedule: {},
        totals_data: {
          subtotal: safeFix(orderBreakdown.subtotal),
          packaging_fee: safeFix(orderBreakdown.packagingFee || 0),
          delivery_fee: safeFix(orderBreakdown.deliveryFee),
          gst: safeFix(orderBreakdown.gst),
          pst: safeFix(orderBreakdown.pst),
          total_amount: safeFix(orderBreakdown.grandTotal),
          ...(orderBreakdown.discount ? { discount: safeFix(orderBreakdown.discount) } : {}),
          ...(orderBreakdown.originalSubtotal ? { original_subtotal: safeFix(orderBreakdown.originalSubtotal) } : {}),
          ...(orderBreakdown.discountRate ? { discount_rate: orderBreakdown.discountRate } : {}),
          ...(orderBreakdown.discountLabel ? { discount_label: orderBreakdown.discountLabel } : {}),
        },
        subtotal: safeFix(orderBreakdown.subtotal),
        discount: orderBreakdown.discount ? safeFix(orderBreakdown.discount) : 0,
        delivery_fee: safeFix(orderBreakdown.deliveryFee),
        gst: safeFix(orderBreakdown.gst),
        pst: safeFix(orderBreakdown.pst),
        total_amount: safeFix(orderBreakdown.grandTotal),
        status: 'pending',
        payment_status: 'paid',
        metadata: {
          user_agent: navigator.userAgent,
          platform: 'web',
          language: i18n.language,
          order_type: orderType,
          fulfillment_mode: fulfillmentMode,
          cart_items_count: cartItems.length,
        },
      };

      try {
        const { data, error } = await supabase
          .from('orders')
          .insert([orderPayload])
          .select();

        if (error) {
          throw error;
        }

        if (!data || data.length === 0 || !data[0].id) {
          throw new Error('Order was not created — no data returned from database');
        }

        navigate('success', { replace: true });
        return;
      } catch (error) {
        alert(t('errors.order_failed') + `：${error.message || '未知錯誤'}` + '\n\n' + t('errors.retry_hint'));
        throw error;
      }
    }

    // ✅ 1. 建立完整的資料快照（包含日期與餐點資訊）- Weekly mode
    const enrichedItemsData = {};

    Object.keys(mealSelections).forEach(dayId => {
        const daySel = mealSelections[dayId];
        if (!daySel) return;

        // 找出這一天對應的真實日期物件
        const dayDateObj = next7Days.find(d => d.id === parseInt(dayId));

        enrichedItemsData[dayId] = {
            // 🔥 關鍵：使用預先計算好的溫哥華時間日期
            date_info: {
                date_string: dayDateObj?.dateString || '',
                weekday: dayDateObj?.weekday || '',
                full_date: dayDateObj?.fullDate || '',

                // 🔥 使用預先計算的 isoDateStr（溫哥華時區）
                iso_date: dayDateObj?.isoDateStr || '',

                // 🔥 明確標記時區（方便除錯和未來擴展）
                timezone: RESTAURANT_TIMEZONE
            }
        };

        ['lunch', 'dinner'].forEach(type => {
            const selection = daySel[type];
            if (selection) {
                const mealId = typeof selection === 'object' ? selection.id : selection;
                const originalMeal = menuOptions.find(m => m.id === mealId);

                if (originalMeal) {
                    const modifiers = (typeof selection === 'object' && Array.isArray(selection.modifiers))
                        ? selection.modifiers
                        : [];

                    // ✅ 完整保存餐點資訊
                    enrichedItemsData[dayId][type] = {
                        id: originalMeal.id,
                        name: originalMeal.name,
                        name_en: originalMeal.name_en || originalMeal.name,
                        name_zh: originalMeal.name_zh || originalMeal.name,
                        description: originalMeal.description || '',
                        price: originalMeal.price,
                        image_url: originalMeal.image_url || '',
                        emoji: originalMeal.emoji || '',
                        categories: originalMeal.categories || [],
                        tax_gst: originalMeal.tax_gst || false,
                        tax_pst: originalMeal.tax_pst || false,
                        modifiers: modifiers.map(mod => ({
                            id: mod.id,
                            name: mod.name,
                            name_en: mod.name_en || mod.name,
                            name_zh: mod.name_zh || mod.name,
                            alternate_name: mod.alternate_name || mod.name,
                            price: mod.price || 0,
                            modifier_group_id: mod.modifier_group_id || null,
                            description: mod.description || ''
                        }))
                    };
                }
            }
        });
    });

    // 初始化配送追蹤狀態
    const initialDeliveryTracking = {};
    Object.keys(enrichedItemsData).forEach(dayId => {
        const dayData = enrichedItemsData[dayId];
        if (dayData.lunch) {
            initialDeliveryTracking[`${dayId}_lunch`] = {
                status: 'pending',
                created_at: new Date().toISOString()
            };
        }
        if (dayData.dinner) {
            initialDeliveryTracking[`${dayId}_dinner`] = {
                status: 'pending',
                created_at: new Date().toISOString()
            };
        }
    });

    try {
      // 查詢座標
      let coords = null;
      if (deliveryAddress && city) {
          const fullAddressStr = `${deliveryAddress}, ${city}, ${province}`;
          coords = await getCoordinatesFromAddress(fullAddressStr);
      }

      const newOrderNumber = generateOrderNumber();
      // ✅ 2. 組裝訂單資料
      const orderPayload = {
        // === 基本資訊 ===
        user_id: user?.id || null,
        order_number: newOrderNumber,
        booking_mode: bookingMode,

        // === 聯絡資訊 ===
        contact_info: {
          name: paymentFormData.contactName,
          email: paymentFormData.email || user?.email || '',
          phone: paymentFormData.contactPhone,
          payment_method: 'credit_card',
          card_holder: paymentFormData.cardHolder,
          stripe_payment_intent_id: stripePaymentIntentId || null,
        },

        // === 配送資訊 ===
        delivery_info: {
          address: deliveryAddress,
          unit: unitNumber,
          city: city,
          postal_code: postalCode,
          province: province,
          zone_id: matchedZone?.id || null,
          zone_name: matchedZone?.name || null,
          delivery_notes: deliveryNotes || paymentFormData.deliveryNotes || '',
          lat: coords ? coords.lat : null,
          lng: coords ? coords.lng : null
        },

        // === 配送追蹤欄位 ===
        delivery_tracking: initialDeliveryTracking,

        // === 餐點資料 (含日期) ===
        items_data: enrichedItemsData,

        // === 配送時間 ===
        delivery_schedule: selectedTime,

        // === 費用資料 ===
        totals_data: {
          subtotal: safeFix(orderBreakdown.subtotal),
          delivery_fee: safeFix(orderBreakdown.deliveryFee),
          gst: safeFix(orderBreakdown.gst),
          pst: safeFix(orderBreakdown.pst),
          total_amount: safeFix(orderBreakdown.grandTotal)
        },

        // === 分開的費用欄位 ===
        subtotal: safeFix(orderBreakdown.subtotal),
        delivery_fee: safeFix(orderBreakdown.deliveryFee),
        gst: safeFix(orderBreakdown.gst),
        pst: safeFix(orderBreakdown.pst),
        total_amount: safeFix(orderBreakdown.grandTotal),

        // === 狀態 ===
        status: 'accepted',
        payment_status: 'paid',

        // === 元數據 ===
        metadata: {
          user_agent: navigator.userAgent,
          platform: 'web',
          selected_days_count: selectedDays.length,
          has_modifiers: Object.values(enrichedItemsData).some(day =>
            (day.lunch?.modifiers?.length > 0 || day.dinner?.modifiers?.length > 0)
          )
        }
      };

      // ✅ 3. 寫入 Supabase
      const { data, error } = await supabase
        .from('orders')
        .insert([orderPayload])
        .select();

      if (error) {
        throw error;
      }

      if (!data || data.length === 0 || !data[0].id) {
        throw new Error('Order was not created — no data returned from database');
      }

      navigate('success', { replace: true });

    } catch (error) {
      let errorMessage = t('errors.order_failed');
      if (error.message?.includes('violates')) {
        errorMessage += t('errors.data_format');
      } else if (error.message?.includes('permission')) {
        errorMessage += t('errors.permission');
      } else if (error.code === 'PGRST204') {
        errorMessage += t('errors.db_column');
      } else {
        errorMessage += `：${error.message || '未知錯誤'}`;
      }

      alert(errorMessage + '\n\n' + t('errors.retry_hint'));
      throw error;
    }
  };

  useEffect(() => {
    if (isLoggedIn && pendingAction === 'goToPayment') {
      setPendingAction(null);
      navigate('payment', { replace: true });
    }
    if (isLoggedIn && pendingAction === 'goToConfirm') {
      setPendingAction(null);
      navigate('confirm', { replace: true });
    }
  }, [isLoggedIn, pendingAction, navigate]);

  // 登入後恢復購物車
  useEffect(() => {
    if (isLoggedIn && bookingMode === 'single') {
      const pendingCart = localStorage.getItem('pending_cart');
      const pendingOrderType = localStorage.getItem('pending_order_type');

      if (pendingCart) {
        try {
          const cartData = JSON.parse(pendingCart);
          if (Array.isArray(cartData) && cartData.length > 0) {
            setCartItems(cartData);
            if (pendingOrderType) {
              setOrderType(pendingOrderType);
            }
            // 清除暫存
            localStorage.removeItem('pending_cart');
            localStorage.removeItem('pending_order_type');
            // 導向結帳頁
            navigate('confirm', { replace: true });
          }
        } catch (e) {
          localStorage.removeItem('pending_cart');
          localStorage.removeItem('pending_order_type');
        }
      }
    }
  }, [isLoggedIn, bookingMode, navigate, setCartItems, setOrderType]);

  const resetOrder = () => {
    setSelectedDays(bookingMode === 'single' ? [0] : []);
    setMealSelections({});
    setSelectedTime({});
    setDeliveryAddress('');
    setUnitNumber('');
    setCity('');
    setPostalCode('');
    setMatchedZone(null);
    // Single mode 重置
    setCartItems([]);
    setOrderType('pickup');
    setFulfillmentMode('instant');
    setScheduledTime(null);
    setScheduledDate(null);
    setDeliveryNotes('');
    navigate('/');
  };

  // --- Context 傳遞 ---
  const outletContext = {
    // 基礎資料
    next7Days,
    menuOptions,
    categoriesList: categoriesData,
    modifierGroupsList: modifierGroupsData,

    // 配送區域資料
    allZones,
    matchedZone,
    detectZone,
    setMatchedZone,

    // 訂單狀態
    bookingMode,
    MIN_DAYS,
    MAX_DAYS,
    selectedDays,
    toggleDay,
    removeDay,
    deliveryAddress, setDeliveryAddress,
    unitNumber, setUnitNumber,
    city, setCity,
    postalCode, setPostalCode,
    province, setProvince,
    mealSelections,
    selectMealForDay,
    selectedTime,
    setSelectedTime,

    // 操作函式
    calculateTotal,
    handleMealSelectionNext,
    handleSubmitTime,
    handlePayment,
    resetOrder,
    isLoggedIn,
    requestLogin,
    user,

    // 全單折扣
    globalDiscount,
    packagingFee,
    needCutlery, setNeedCutlery,

    // Single mode 購物車
    orderType, setOrderType,
    cartItems, setCartItems,
    fulfillmentMode, setFulfillmentMode,
    scheduledTime, setScheduledTime,
    scheduledDate, setScheduledDate,
    deliveryNotes, setDeliveryNotes,
    addToCart,
    updateCartItemQuantity,
    removeCartItem,
    calculateCartTotal,
  };

  return (
    <>
      <RestaurantBookingStyles />
      <div className="booking-container">
        {envError && (
          <div className="env-warning">
            {t('env_warning')}
          </div>
        )}

        {isLoading ? (
          <div className="loading-container">
            <p>{t('loading')}</p>
          </div>
        ) : fetchError ? (
          <div className="loading-container">
            <p className="error-message">{fetchError}</p>
            <button
              onClick={() => window.location.reload()}
              style={{marginTop: '1rem', padding: '8px 16px', cursor: 'pointer'}}
            >
              {t('reload')}
            </button>
          </div>
        ) : (
          <Outlet context={outletContext} />
        )}
      </div>
    </>
  );
};

export default RestaurantBooking;
